import { useMemo } from "react";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { ExtractedTextResponse, PreviewableDocument } from "../DocumentPreview.types";
import { getExtractedTextUrl } from "../DocumentPreview.hooks";

/**
 * Whether an extracted-text fallback can even be asked for: it is keyed on the indexed document version,
 * and a document previewed through a custom url has no version on this side.
 */
export function supportsExtractedText(document: PreviewableDocument | undefined): boolean {
    return !!document?.documentVersionId && document.entityType !== undefined && !!document.entityId;
}

function splitIntoPages(content: string, pageOffsets: Array<number>): Array<string> {
    if (pageOffsets.length === 0) {
        return [content];
    }

    // Offsets mark where each page starts, so each page runs to the next offset.
    const boundaries = [...pageOffsets, content.length];
    return boundaries.slice(0, -1).map((start, index) => content.substring(start, boundaries[index + 1]));
}

/**
 * localText is text the caller already holds for a document that has no indexed version to fetch it from,
 * such as a file uploaded but not yet stored. When given, nothing is fetched.
 */
export function useExtractedText(document: PreviewableDocument | undefined, enabled: boolean, localText?: ExtractedTextResponse) {
    const url = document && supportsExtractedText(document) && !localText ? getExtractedTextUrl(document) : "";
    const query = useDotLegalQuery<ExtractedTextResponse>(url, { enabled: enabled && url.length > 0 });

    const data = localText ?? query.data;
    const isLoading = localText ? false : query.isLoading;

    // Memoised for identity, not for the slicing cost: `pages` is a dependency of TextView's observer and
    // citation effects, so a fresh array on every parent render would rebuild the IntersectionObserver and
    // re-run the highlight pass on each zoom click.
    const pages = useMemo(() => (data ? splitIntoPages(data.content, data.pageOffsets ?? []) : undefined), [data]);

    return {
        pages,
        content: data?.content,
        pageOffsets: data?.pageOffsets ?? [],
        isLoading,
        // A version that has not been indexed yet answers with empty content, which is the "nothing to
        // select" case rather than a failure worth taking the viewer away for.
        hasNoExtractedText: !isLoading && !!data && !/\S/.test(data.content),
    };
}
